import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { JsonRpcProvider } from "ethers";
import { disconnect } from "@wagmi/core";
import { useLogin, usePrivy } from "@privy-io/react-auth";
import { useAccount, useBalance, useConfig, useSwitchChain } from "wagmi";
import { useAppStore } from "../store/appStore";
import { toast } from "react-toastify";
import { formatAddress } from "../utils/formatAddres";
import { useAccountBalances } from "../hooks/useAccountBalances";
import {sidebarTokens} from "../constants/sidebarTokens";
import { useTokensStore } from "../store/tokensStore";
import { tokensService } from "../services/tokensService";
import { nativeToken } from '../constants/nativeToken';
import { currentChain } from '../connectors/wagmiConfig';

const InitialContextValues = {
  walletAddress: undefined,
  jsonRpcProvider: undefined,
  checkWalletAddress: () => false,
  login: () => {},
  handleDisconnect: () => {},
  getUserTokensBalance: async () => {},
  factoryFees: {
    xbrFeeValue: 0,
    getFactoryFees: () => {},
    stakingFeeValues: null,
    unstakingFeeValues: null,
    allowance: undefined,
    poolCreationFeeValue: "",
    isLoading: false,
    shouldApprove: true,
    isApproveLoading: false,
    approveTokens: async () => {},
    createPool: async () => {},
    getAllowance: async () => {},
    checkApprove: async () => undefined,
    getProfitSharingFeeDenominator: async () => {},
    profitSharingFeeDenominator: 0,
  },
  refetchNativeCurrencyBalance: async () => {},
};

export const PoolContext = createContext(InitialContextValues);

export const useContextProvider = () => useContext(PoolContext);

export const PoolContextProvider = ({ children }) => {
  const { address, chain } = useAccount();
  const [walletAddress, setWalletAddress] = useState();
  const [nativeCurrency, setNativeCurrency] = useState(null);
  const config = useConfig();
		const { switchChain } = useSwitchChain({
			config,
			mutation: {
				onSuccess: () => {
				},
				onError: error => {
					console.error('Error Switching Network', error);
				},
			},
		});

  const jsonRpcProvider = useMemo(
    () => new JsonRpcProvider(process.env.REACT_APP_RPC_URL),
    [address]
  );

  const { login } = useLogin({
    onError: async error => {
      console.error("Login error:", error);

      if (error?.includes("exited_auth_flow")) {
        return;
      }

      handleDisconnect();
    },
  });

  const { logout, authenticated, user } = usePrivy();
  const { setIsOpenedUserMenu } = useAppStore();
  const { setUserTokens } = useTokensStore();
  const { getUserTokensBalanse } = useAccountBalances(jsonRpcProvider);

  const getUserTokensBalance = useCallback(async () => {
    if (!walletAddress) return;

    try {
      const userTokens = [];

      const addresses = sidebarTokens.map(token => token.address);

      const [tokensWithBalance, prices] = await Promise.all([
        getUserTokensBalanse(sidebarTokens, walletAddress),
        tokensService.getTokensPrices(addresses),
      ]);

      tokensWithBalance.forEach((token, i) => {
        const price = prices?.[i]?.data?.data?.attributes?.price_usd;
        const valueInUsdt = token.value * (price || 0);

        userTokens.push({
          ...token,
          valueInUsdt,
        });
      });

      setUserTokens(userTokens);
    } catch (error) {
      console.error(error);

      setUserTokens([]);
    }
  }, [jsonRpcProvider, walletAddress]);

  useEffect(() => {
    if (authenticated && user) {
      const userWalletAddress = user?.wallet?.address;

      setWalletAddress(userWalletAddress);
    }

    if (!authenticated || !user) {
      setWalletAddress(undefined);
    }
  }, [authenticated, user]);


	useEffect(() => {
		if (authenticated && user && chain?.id !== currentChain.id) {
				switchChain({chainId: currentChain.id})
		}
	}, [authenticated, user, chain])

  useEffect(() => {
    if (!walletAddress || !jsonRpcProvider) return;

    getUserTokensBalance();
  }, [walletAddress, jsonRpcProvider]);

  const handleDisconnect = async () => {
    try {
      await logout();

      await disconnect(config);
      setIsOpenedUserMenu(false);
    } catch (error) {
      console.error("Disconnection error:", error);
    }
  };

  const checkWalletAddress = useCallback(() => {
    if (!walletAddress) {
      toast.error("You do not have any wallets yet");

      return false;
    }

    if (
      !address ||
      (walletAddress && address?.toLowerCase() !== walletAddress?.toLowerCase())
    ) {
      toast(`Please connect your wallet ${formatAddress(walletAddress)}`);

      return false;
    }

    return true;
  }, [walletAddress, address]);

  const { data, refetch: refetchNativeCurrencyBalance } = useBalance({
    address: walletAddress,
    chainId: currentChain.id
  });

  useEffect(() => {
    if (!data) {
      setNativeCurrency(null);

      return;
    }

    const getNativeToken = async () => {
      const prices = await tokensService.getTokensPrices([
        nativeToken.address,
      ]);

      const price = prices?.[0]?.data?.data?.attributes?.price_usd;

      setNativeCurrency({
        ...data,
        valueInUsdt: parseFloat(data.formatted) * price || 0,
        iconUrl: nativeToken.iconUrl,
        priceFromAddress: nativeToken.address,
      });
    };

    getNativeToken();
  }, [data]);

  const value = {
    jsonRpcProvider,
    nativeCurrency,
    refetchNativeCurrencyBalance,
    login,
    handleDisconnect,
    walletAddress,
    checkWalletAddress,
    getUserTokensBalance
  };

  return <PoolContext.Provider value={value}>{children}</PoolContext.Provider>;
};
